import {
  useRef,
  useState,
  useCallback,
  useEffect,
  MutableRefObject,
} from 'react';
import { useEditor, Editor } from '@tiptap/react';
import { JSONContent, EditorEvents } from '@tiptap/core';

import {
  getExtensions,
  isRichtextEmpty,
  removeAnchorElements,
  removeInlineFontStyle,
  Settings,
} from './richtext.utils';
import {
  TAB,
  ARROW_LEFT,
  ARROW_RIGHT,
  ARROW_DOWN,
  tabbable,
  ENTER,
  findDropdownButtons,
} from 'approot/shared/ally/keyboard.hooks';
import { isMacPlatform } from 'approot/shared/ally/is-mac-platform';
import { LINK_MODAL_ID } from './richtext-link-modal';

type Props = {
  onChange: (editor: JSONContent) => void;
  initialValue?: JSONContent;
  onFocus?: (focusProps: EditorEvents['focus']) => void;
  onBlur?: (blurProps: EditorEvents['blur']) => void;
  settings: Settings;
};

type OnDemandProps = {
  onChange: (editor: JSONContent) => void;
  onFocus?: (focusProps: EditorEvents['focus']) => void;
  onBlur?: (blurProps: EditorEvents['blur']) => void;
  onCreateEditor?: (initialValue?: JSONContent) => void;
  settings: Settings;
};

export const useRichtext = ({
  initialValue,
  onFocus,
  onBlur,
  onChange,
  settings,
}: Props) => {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [showToolbar, setShowToolbar] = useState<boolean>(false);

  const editor = useEditor({
    content: isRichtextEmpty(initialValue) ? undefined : initialValue,
    extensions: getExtensions(settings),
    editorProps: {
      transformPastedText(text) {
        let doc = removeInlineFontStyle(text);
        doc = removeAnchorElements(doc);
        return doc.documentElement.innerText;
      },
      transformPastedHTML(html) {
        let doc = removeInlineFontStyle(html);
        doc = removeAnchorElements(doc);
        return doc.documentElement.outerHTML;
      },
    },
    onUpdate: ({ editor }) => {
      // prevent empty tags from saving
      onChange(editor.isEmpty ? {} : editor.getJSON());
    },
    onFocus: props => {
      onFocus && onFocus(props);
      if (!showToolbar) setShowToolbar(true);
    },
    onBlur: props => {
      onBlur && onBlur(props);
    },
  });

  const handleDocumentClick = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      if (hideToolbar(e, showToolbar, toolbarRef, editor, settings)) {
        setShowToolbar(false);
      }
    },
    [showToolbar, editor, settings]
  );

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keyup', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keyup', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return {
    editor,
    toolbarRef,
    showToolbar,
  };
};

export const useOnDemandRichtext = ({
  onFocus,
  onBlur,
  onChange,
  onCreateEditor,
  settings,
}: OnDemandProps) => {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [showToolbar, setShowToolbar] = useState<boolean>(false);
  const [editor, setEditor] = useState<Editor | null>(null);

  const forceUpdate = useForceUpdate();

  const createEditor = useCallback(
    (initialValue: JSONContent) => {
      if (editor) editor.destroy();

      const isInitialValueEmpty = isRichtextEmpty(initialValue);

      const newEditor = new Editor({
        extensions: getExtensions(settings),
        content: isInitialValueEmpty ? undefined : initialValue,
        editorProps: {
          transformPastedText(text) {
            let doc = removeInlineFontStyle(text);
            doc = removeAnchorElements(doc);
            return doc.documentElement.innerText;
          },
          transformPastedHTML(html) {
            let doc = removeInlineFontStyle(html);
            doc = removeAnchorElements(doc);
            return doc.documentElement.outerHTML;
          },
        },
        onUpdate: ({ editor }) => {
          // prevent empty tags from saving
          onChange(editor.isEmpty ? {} : editor.getJSON());
        },
        onFocus: props => {
          onFocus && onFocus(props);
          setShowToolbar(true);
        },
        onBlur: props => {
          onBlur && onBlur(props);
        },
        onTransaction: () => {
          // taken from https://github.com/ueberdosis/tiptap/blob/main/packages/react/src/useEditor.ts
          requestAnimationFrame(() => {
            forceUpdate();
          });
        },
      });

      setEditor(newEditor);
      newEditor.chain().focus();

      onCreateEditor && onCreateEditor();
    },
    [editor, onBlur, onChange, onCreateEditor, onFocus, forceUpdate, settings]
  );

  const handleDocumentClick = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      if (hideToolbar(e, showToolbar, toolbarRef, editor, settings)) {
        setShowToolbar(false);
      }
    },
    [showToolbar, editor, settings]
  );

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keyup', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keyup', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return {
    createEditor,
    editor,
    toolbarRef,
    showToolbar,
  };
};

function hideToolbar(
  e: MouseEvent | KeyboardEvent,
  showToolbar: boolean,
  toolbarRef: MutableRefObject<HTMLDivElement | null>,
  editor: Editor | null,
  settings: Settings
): boolean {
  if (showToolbar && toolbarRef.current && !editor?.isFocused) {
    const clickedElement = e.target as HTMLElement;

    const clickedWithinToolbar = toolbarRef.current.contains(clickedElement);

    if (!clickedWithinToolbar) {
      if (settings.canAddLinks) {
        const linkModal = document.getElementsByClassName(LINK_MODAL_ID)?.[0];

        if (
          linkModal?.contains(clickedElement) ||
          // clickedElement can be the ReactModal dialog when using the keyboard
          clickedElement.className.includes('ReactModal')
        ) {
          return false;
        }
      }

      return true;
    }
  }

  return false;
}

// taken from https://github.com/ueberdosis/tiptap/blob/main/packages/react/src/useEditor.ts
function useForceUpdate() {
  const [, setValue] = useState<number>(0);
  return () => setValue(value => value + 1);
}

export function findToolbarButtons(
  element: HTMLElement | null
): HTMLButtonElement[] | [] {
  if (element) {
    const nodes = element.querySelectorAll<HTMLButtonElement>(
      'button:not([disabled])'
    );
    return Array.from(nodes).filter(tabbable);
  }
  return [];
}

// the toolbar's ref is passed in rather than the inner DOM element
// so that on demand editors (e.g. table cells) have time to wire up the element before this code runs
export const useToolbarTrap = (
  toolbarRef: MutableRefObject<HTMLDivElement | null>,
  handleTabToExit: () => void
) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!toolbarRef) return;

      const currentElement = document.activeElement as HTMLElement;
      const isWithinDropdown = currentElement.dataset.dropdownItem === 'true';

      // this is handled in the dropdown component
      if (isWithinDropdown) return;

      const buttons = findToolbarButtons(toolbarRef.current);
      const isDropdownTrigger = currentElement.hasAttribute(
        'data-dropdown-trigger'
      );

      switch (e.key) {
        case ARROW_LEFT:
          e.preventDefault();

          if (buttons.length > 1) {
            const currentIndex = buttons.findIndex(
              f => f === document.activeElement
            );

            if (currentIndex > 0) {
              (buttons[currentIndex - 1] as HTMLElement).focus();
            }
          }

          break;
        case ARROW_RIGHT:
          e.preventDefault();

          if (buttons.length > 1) {
            const currentIndex = buttons.findIndex(
              f => f === document.activeElement
            );

            if (currentIndex !== -1 && currentIndex < buttons.length - 1) {
              (buttons[currentIndex + 1] as HTMLElement).focus();
            }
          }

          break;
        case TAB:
          if (e.shiftKey) return;

          if (handleTabToExit) {
            if (buttons.length !== -1) {
              const currentIndex = buttons.findIndex(
                f => f === document.activeElement
              );

              if (currentIndex === buttons.length - 1) {
                e.preventDefault();
                handleTabToExit();
                break;
              }
            }
          }

          break;
        case ENTER:
        case ARROW_DOWN:
          if (isDropdownTrigger) {
            e.preventDefault();
            currentElement.click();

            const dropdownContainerId =
              currentElement.dataset.dropdownContainerId;
            const dropdownContainer =
              dropdownContainerId &&
              document.getElementById(dropdownContainerId);
            const dropdownContents =
              dropdownContainer && findDropdownButtons(dropdownContainer);

            if (dropdownContents && dropdownContents.length > 0) {
              dropdownContents[0].focus();
            }
          }
          break;
        default:
          break;
      }
    },
    [toolbarRef, handleTabToExit]
  );

  useEffect(() => {
    const refCopy = toolbarRef?.current;

    if (refCopy) {
      refCopy.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      // clean up listeners
      if (refCopy) {
        refCopy.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [handleKeyDown, toolbarRef]);
};

export const useToolbarJump = (
  toolbarRef?: MutableRefObject<HTMLElement | null>
) => {
  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      const isMac = isMacPlatform();

      if (
        (isMac ? e.metaKey : e.ctrlKey) &&
        e.key === 'e' &&
        !!(e.target as HTMLDivElement)?.hasAttribute('contenteditable') &&
        toolbarRef?.current
      ) {
        e.preventDefault();

        const firstButton = findToolbarButtons(toolbarRef.current)?.[0];
        if (firstButton) firstButton.focus();
      }
    },
    [toolbarRef]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown, false);
    return () => {
      document.removeEventListener('keydown', handleKeydown, false);
    };
  }, [handleKeydown]);
};

export const useRichTextAddAria = ({
  ariaLabelledBy,
  ariaDescribedBy,
  ref,
  role,
}: {
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  ref: HTMLDivElement | null;
  role?: string;
}) => {
  useEffect(() => {
    if (!ariaLabelledBy && !ariaDescribedBy) return;

    const element = ref;
    if (element) {
      const firstChild = element.firstElementChild;
      if (firstChild) {
        if (ariaLabelledBy && !firstChild.hasAttribute('aria-labelledby')) {
          firstChild.setAttribute('aria-labelledby', ariaLabelledBy);
        }
        if (ariaDescribedBy && !firstChild.hasAttribute('aria-describedby')) {
          firstChild.setAttribute('aria-describedby', ariaDescribedBy);
        }
        if (role && !firstChild.hasAttribute('role')) {
          firstChild.setAttribute('role', role);
        }
      }
    }
  }, [ariaLabelledBy, ariaDescribedBy, ref, role]);
};
